"use client";

import Image from "next/image";
import Link from "next/link";
import {
  ArrowRight,
  CheckCircle2,
  HeartHandshake,
  MapPin,
  Search,
  ShoppingBag,
  Store,
  Truck,
  Users,
} from "lucide-react";

const ABOUT_CONTAINER = "mx-auto w-full max-w-[1180px] px-6 sm:px-8 lg:px-10";
const SECTION_Y = "py-8 md:py-12";
const BODY_TEXT = "text-base leading-7 text-slate-600";
const CARD_TEXT = "text-sm leading-6 text-slate-600";

const SERVE_CARDS = [
  {
    title: "Customers",
    copy: "Discover nearby shops, browse available items, place orders online, and keep track of pickup or delivery details.",
    icon: Users,
  },
  {
    title: "Local businesses",
    copy: "List products, manage orders, share updates, and give nearby customers a simpler way to shop online.",
    icon: Store,
  },
  {
    title: "Neighborhoods",
    copy: "Make local discovery easier and help more spending stay connected to the businesses that give each neighborhood its character.",
    icon: HeartHandshake,
  },
];

const HOW_IT_WORKS = [
  {
    title: "Discover",
    copy: "Find local shops and products nearby.",
    icon: Search,
  },
  {
    title: "Order",
    copy: "Buy online through YourBarrio with a clear checkout flow.",
    icon: ShoppingBag,
  },
  {
    title: "Pickup or delivery",
    copy: "Pick up in store, or choose delivery when a business offers it.",
    icon: Truck,
  },
];

const TRUST_ITEMS = [
  {
    title: "Secure checkout",
    description: "Payments handled through Stripe.",
  },
  {
    title: "Order tracking",
    description: "Customers can follow order status.",
  },
  {
    title: "Pickup or delivery options",
    description: "Fulfillment depends on each business.",
  },
  {
    title: "Starting locally in Long Beach",
    description: "Beginning with Long Beach.",
  },
];

function PrimaryButton({ href, children }) {
  return (
    <Link
      href={href}
      className="inline-flex min-h-12 items-center justify-center rounded-xl bg-[var(--color-primary)] px-6 py-3 text-sm font-semibold !text-white shadow-[0_14px_28px_-18px_rgba(var(--brand-rgb),0.7)] transition hover:bg-[#5E2DE0] hover:!text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300/35"
    >
      {children}
    </Link>
  );
}

function SecondaryButton({ href, children }) {
  return (
    <Link
      href={href}
      className="inline-flex min-h-12 items-center justify-center rounded-xl border border-slate-200 bg-white/80 px-6 py-3 text-sm font-semibold text-slate-800 shadow-[0_10px_30px_-24px_rgba(15,23,42,0.25)] transition hover:border-purple-200 hover:text-[rgb(var(--brand-rgb))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300/25"
    >
      {children}
    </Link>
  );
}

function SectionHeading({ eyebrow, title, body, align = "left" }) {
  return (
    <div className={`${align === "center" ? "mx-auto text-center" : ""} max-w-3xl`}>
      {eyebrow ? (
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[rgb(var(--brand-rgb))]">
          {eyebrow}
        </p>
      ) : null}
      <h2 className="text-3xl font-semibold tracking-[-0.045em] text-slate-950 sm:text-[2.25rem]">
        {title}
      </h2>
      {body ? <p className={`mt-4 ${BODY_TEXT}`}>{body}</p> : null}
    </div>
  );
}

function AboutHero() {
  return (
    <section className="relative w-full pb-8 md:pb-10">
      <div
        className="relative h-[340px] w-full overflow-hidden bg-cover bg-center sm:h-[390px] lg:h-[520px]"
        role="img"
        aria-label="A walkable neighborhood shopping street with local storefronts and outdoor displays."
        style={{ backgroundImage: "url('/images/homepage/hero-main.png')" }}
      >
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(15,23,42,0.02)_0%,rgba(15,23,42,0.16)_48%,rgba(15,23,42,0.76)_100%)]" />
        <div className="absolute inset-x-0 bottom-0">
          <div className={`${ABOUT_CONTAINER} pb-7 sm:pb-9 lg:pb-11`}>
            <h1 className="max-w-3xl text-4xl font-semibold tracking-[-0.055em] !text-white sm:text-5xl lg:text-6xl">
              About YourBarrio
            </h1>
          </div>
        </div>
      </div>
    </section>
  );
}

function CommerceStoryVisual() {
  return (
    <div className="mx-auto w-full overflow-hidden bg-white shadow-[0_26px_70px_-58px_rgba(15,23,42,0.55)] ring-1 ring-slate-200/70 lg:mx-0">
      <div className="relative aspect-[5/4]">
        <Image
          src="/images/about/local-commerce-about.png"
          alt="Independent local shops in a walkable neighborhood."
          fill
          className="object-cover"
          sizes="(max-width: 1024px) 100vw, 560px"
        />
      </div>
    </div>
  );
}

function HowItWorksStepper() {
  return (
    <div className="mx-auto mt-7 max-w-5xl">
      <div className="relative grid gap-5 md:grid-cols-3 md:gap-6">
        <div className="absolute left-4 top-4 bottom-4 hidden w-px bg-slate-200 max-md:block" />
        <div className="absolute left-[calc(16.666%+1rem)] right-[calc(16.666%+1rem)] top-4 hidden h-px bg-slate-200 md:block" />
        {HOW_IT_WORKS.map(({ title, copy, icon: Icon }, index) => (
          <article key={title} className="relative flex gap-3 md:block">
            <div className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#f1ebff] text-sm font-semibold text-[rgb(var(--brand-rgb))] ring-4 ring-white">
              {index + 1}
            </div>
            <div className="min-w-0 md:mt-4">
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-[rgb(var(--brand-rgb))]" aria-hidden="true" />
                <h3 className="text-base font-semibold tracking-[-0.025em] text-slate-950">
                  {title}
                </h3>
              </div>
              <p className={`mt-1.5 max-w-[18rem] ${CARD_TEXT}`}>{copy}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

export default function AboutPage() {
  return (
    <main className="min-h-screen overflow-hidden bg-white text-slate-950">
      <AboutHero />

      <section className={`${ABOUT_CONTAINER} ${SECTION_Y}`}>
        <div className="grid gap-8 border-b border-slate-200/80 pb-10 md:grid-cols-[0.72fr_1fr] md:pb-12">
          <SectionHeading title="Make local commerce easier to discover, manage, and support." />
          <div className="space-y-4 md:pt-2">
            <p className={BODY_TEXT}>
              YourBarrio connects customers with nearby businesses so they can discover
              products, order online, and support local shops in their neighborhood.
            </p>
            <p className={BODY_TEXT}>
              We believe neighborhood businesses should have practical tools to sell
              online without losing the local connection that makes them special.
            </p>
          </div>
        </div>
      </section>

      <section className={`${ABOUT_CONTAINER} pt-4 pb-8 md:pt-5 md:pb-12`}>
        <div className="grid items-start gap-7 lg:grid-cols-[1.04fr_0.96fr] lg:gap-9">
          <div className="order-2 lg:order-1">
            <CommerceStoryVisual />
          </div>
          <div className="order-1 lg:order-2 lg:max-w-[33rem]">
            <SectionHeading
              eyebrow="Why we are building this"
              title="Built to help local shops compete online"
              body="Many neighborhood businesses have great products but limited time, budget, or tools to manage online selling. Customers often want to support local shops, but discovering what is available nearby can be difficult. YourBarrio is designed to close that gap by helping shops list products, accept orders, and serve nearby customers through a simpler local marketplace."
            />
          </div>
        </div>
      </section>

      <section className={`${ABOUT_CONTAINER} ${SECTION_Y}`}>
        <SectionHeading
          title="Who we serve"
          align="center"
        />
        <div className="mt-9 grid gap-4 md:grid-cols-3">
          {SERVE_CARDS.map(({ title, copy, icon: Icon }) => (
            <article
              key={title}
              className="flex h-full flex-col rounded-2xl border border-slate-200/80 bg-white/78 p-5 shadow-[0_18px_55px_-48px_rgba(15,23,42,0.34)]"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[rgba(var(--brand-rgb),0.08)] text-[rgb(var(--brand-rgb))]">
                <Icon className="h-5 w-5" aria-hidden="true" />
              </div>
              <h3 className="mt-5 text-lg font-semibold tracking-[-0.03em] text-slate-950">
                {title}
              </h3>
              <p className={`mt-2 ${CARD_TEXT}`}>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="my-4 bg-[rgba(var(--brand-rgb),0.055)] py-10 md:my-6 md:py-12">
        <div className={`${ABOUT_CONTAINER} grid items-center gap-8 md:grid-cols-[0.72fr_1fr]`}>
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white text-[rgb(var(--brand-rgb))] shadow-[0_14px_35px_-28px_rgba(15,23,42,0.4)]">
              <MapPin className="h-7 w-7" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[rgb(var(--brand-rgb))]">
                Local rollout
              </p>
              <h2 className="mt-2 text-3xl font-semibold tracking-[-0.045em] text-slate-950">
                Starting in Long Beach
              </h2>
            </div>
          </div>
          <div>
            <p className={BODY_TEXT}>
              YourBarrio is beginning with a focused rollout in Long Beach,
              California. We are building toward a marketplace that reflects real
              local businesses, real inventory, and practical pickup or delivery
              options.
            </p>
            <p className={`mt-4 ${BODY_TEXT}`}>
              As the platform grows, our goal is to help more neighborhoods discover
              and support the businesses around them.
            </p>
          </div>
        </div>
      </section>

      <section className={`${ABOUT_CONTAINER} ${SECTION_Y}`}>
        <SectionHeading eyebrow="How it works" title="Simple steps for shopping nearby" align="center" />
        <HowItWorksStepper />
      </section>

      <section className={`${ABOUT_CONTAINER} ${SECTION_Y}`}>
        <div className="grid gap-9 border-t border-slate-200 pt-10 lg:grid-cols-[0.82fr_1fr]">
          <SectionHeading
            eyebrow="Trust"
            title="Built around practical, factual confidence"
            body="YourBarrio is early-stage, so our credibility comes from clear product behavior, secure payments, and honest expectations for customers and businesses."
          />
          <div className="self-start rounded-[22px] border border-slate-200/80 bg-white/82 p-5 shadow-[0_20px_58px_-52px_rgba(15,23,42,0.34)] md:p-6">
            <div className="grid gap-5 sm:grid-cols-2">
              {TRUST_ITEMS.map(({ title, description }) => (
                <div key={title} className="flex items-start gap-3">
                  <CheckCircle2
                    className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--brand-rgb))]"
                    aria-hidden="true"
                  />
                  <div>
                    <p className="text-sm font-semibold leading-5 text-slate-900">{title}</p>
                    <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className={`${ABOUT_CONTAINER} pt-8 pb-14 md:pt-10 md:pb-16`}>
        <div className="rounded-[24px] border border-purple-200/70 bg-[linear-gradient(135deg,#ffffff_0%,#f4f0ff_100%)] p-6 shadow-[0_28px_80px_-64px_rgba(79,70,229,0.55)] md:p-10">
          <div className="grid items-center gap-8 md:grid-cols-[1fr_auto]">
            <div>
              <h2 className="text-3xl font-semibold tracking-[-0.045em] text-slate-950">
                Ready to support local?
              </h2>
              <p className={`mt-3 max-w-2xl ${BODY_TEXT}`}>
                Explore nearby products and help local businesses sell online, one
                order at a time.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row md:justify-end">
              <PrimaryButton href="/listings">
                <span className="inline-flex items-center gap-2">
                  Start exploring
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </span>
              </PrimaryButton>
              <SecondaryButton href="/business">For businesses</SecondaryButton>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
